/* VIYAN: Prada-style mobile bottom bar + pull-up sheet for the
   product page.

   On mobile the product info stays in the normal document flow after
   the gallery, so scrolling past the photos reveals it like any other
   page content. A fixed bottom bar (title + price + add-to-cart) is
   shown only while the in-page buy area is still below the viewport;
   once the purchase panel scrolls into view the bar slides away so
   two add-to-carts never show at once. Pulling the bar up (or tapping
   it) temporarily moves the product info into the sheet overlay, and
   closing the sheet puts it back into the page flow. */
(function () {
  'use strict';

  const MOBILE_QUERY = '(max-width: 749px)';
  const DRAG_THRESHOLD_PX = 40;

  function initSheet(sheet) {
    if (sheet.dataset.viyanInit === 'true') return;
    sheet.dataset.viyanInit = 'true';

    const sectionId = sheet.dataset.section;
    const info = document.getElementById('ProductInfo-' + sectionId);
    const header = sheet.querySelector('[data-viyan-sheet-header]');
    const content = sheet.querySelector('[data-viyan-sheet-content]');
    if (!info || !header || !content) return;

    const backdrop = sheet.parentElement.querySelector('[data-viyan-sheet-backdrop]');
    const atcProxy = header.querySelector('[data-viyan-sheet-atc]');
    const priceProxy = header.querySelector('[data-viyan-sheet-price]');
    const placeholder = document.createComment('viyan-sheet-placeholder');
    const mediaQuery = window.matchMedia(MOBILE_QUERY);

    let open = false;
    let barHidden = false;

    const realSubmit = () => document.getElementById('ProductSubmitButton-' + sectionId);

    /* Prada behavior: the fixed bar only exists while the in-page buy
       area is still below the viewport (browsing photos). Once it
       scrolls into view — or past — the bar slides away so two
       add-to-cart buttons never show at once. */
    function applyBarState() {
      sheet.classList.toggle('viyan-sheet--bar-hidden', barHidden && !open);
    }

    let buyObserver = null;
    let observedBuyArea = null;

    function buyAreaTarget() {
      return info.querySelector('.product-form__buttons') || realSubmit();
    }

    function observeBuyArea() {
      if (!('IntersectionObserver' in window)) return;
      const target = buyAreaTarget();
      if (!target || target === observedBuyArea) return;
      if (buyObserver) buyObserver.disconnect();
      observedBuyArea = target;
      buyObserver = new IntersectionObserver(
        (entries) => {
          /* While the sheet overlay is open the buy area sits inside
             the sheet itself; ignore those readings so the pre-open
             scroll position still decides the bar state on close. */
          if (open) return;
          const entry = entries[entries.length - 1];
          const scrolledPast = entry.boundingClientRect.bottom < 0;
          barHidden = entry.isIntersecting || scrolledPast;
          applyBarState();
        },
        /* only count the buy area as visible once it peeks out above
           the fixed bar, not while it is still hidden behind it */
        { rootMargin: '0px 0px -' + header.offsetHeight + 'px 0px' }
      );
      buyObserver.observe(target);
    }

    function moveInfoIntoSheet() {
      if (info.parentElement === content) return;
      info.parentElement.insertBefore(placeholder, info);
      content.appendChild(info);
    }

    function restoreInfoToFlow() {
      if (placeholder.parentNode && info.parentElement === content) {
        placeholder.parentNode.replaceChild(info, placeholder);
      }
    }

    function setOpen(next) {
      open = next;
      if (open) {
        moveInfoIntoSheet();
      }
      sheet.classList.toggle('viyan-sheet--open', open);
      header.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (backdrop) backdrop.hidden = !open;
      document.body.classList.toggle('viyan-sheet-lock', open);
      if (!open) {
        restoreInfoToFlow();
      }
      applyBarState();
    }

    function setBodyPadding() {
      if (mediaQuery.matches && sheet.classList.contains('viyan-sheet--active')) {
        document.body.style.paddingBottom = header.offsetHeight + 'px';
      } else {
        document.body.style.paddingBottom = '';
      }
    }

    function enterMobile() {
      sheet.classList.add('viyan-sheet--active');
      setBodyPadding();
    }

    function exitMobile() {
      setOpen(false);
      sheet.classList.remove('viyan-sheet--active');
      document.body.style.paddingBottom = '';
    }

    header.addEventListener('click', (event) => {
      if (event.target.closest('[data-viyan-sheet-atc]')) return;
      setOpen(!open);
    });
    header.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      if (event.target.closest('[data-viyan-sheet-atc]')) return;
      event.preventDefault();
      setOpen(!open);
    });
    if (backdrop) backdrop.addEventListener('click', () => setOpen(false));

    let touchStartY = null;
    header.addEventListener(
      'touchstart',
      (event) => {
        touchStartY = event.touches[0].clientY;
      },
      { passive: true }
    );
    header.addEventListener(
      'touchmove',
      (event) => {
        if (touchStartY === null) return;
        const delta = touchStartY - event.touches[0].clientY;
        if (delta > DRAG_THRESHOLD_PX && !open) {
          setOpen(true);
          touchStartY = null;
        } else if (delta < -DRAG_THRESHOLD_PX && open) {
          setOpen(false);
          touchStartY = null;
        }
      },
      { passive: true }
    );
    header.addEventListener('touchend', () => {
      touchStartY = null;
    });

    if (atcProxy) {
      atcProxy.addEventListener('click', () => {
        const submit = realSubmit();
        if (submit) submit.click();
      });
    }

    /* Mirror the real form's price and availability onto the fixed
       bar whenever Dawn re-renders after a variant change. The
       proxies live outside `info`, so updating them cannot re-trigger
       this observer. */
    function sync() {
      const priceContainer = info.querySelector('#price-' + sectionId);
      if (priceContainer && priceProxy) {
        const onSale = priceContainer.querySelector('.price--on-sale');
        const item = onSale
          ? priceContainer.querySelector('.price__sale .price-item--sale')
          : priceContainer.querySelector('.price__regular .price-item--regular');
        if (item && item.textContent.trim()) priceProxy.textContent = item.textContent.trim();
      }
      const submit = realSubmit();
      if (submit && atcProxy) {
        atcProxy.disabled = submit.disabled || submit.getAttribute('aria-disabled') === 'true';
        const label = submit.querySelector('span');
        if (label && label.textContent.trim()) atcProxy.textContent = label.textContent.trim();
      }
      /* Dawn may replace the buy-buttons node when it re-renders after
         a variant change — re-attach the visibility observer if so. */
      if (observedBuyArea && !observedBuyArea.isConnected) {
        observedBuyArea = null;
        observeBuyArea();
      }
    }
    new MutationObserver(sync).observe(info, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['disabled', 'aria-disabled'],
    });
    sync();

    function onViewportChange() {
      if (mediaQuery.matches) {
        enterMobile();
      } else {
        exitMobile();
      }
    }
    mediaQuery.addEventListener('change', onViewportChange);
    window.addEventListener('resize', () => {
      setBodyPadding();
      /* the bar height feeds the observer's rootMargin — rebuild it
         so the show/hide boundary stays aligned with the bar */
      observedBuyArea = null;
      observeBuyArea();
    });
    onViewportChange();
    observeBuyArea();
  }

  document.querySelectorAll('.viyan-sheet').forEach(initSheet);
})();
